import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import useExperiencesSortOptions from '../hooks/useExperiencesSortOptions'
import Group from 'components/utils/Group'
import useExperiencesSearchFilters from '../hooks/useExperiencesSearchFilters'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { getExperienceListOfferCount } from 'lib/experiences/experienceUtils'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryDown } from 'components/utils/breakpoint'
import ExperienceDenseSearchList from 'components/Experiences/ExperienceLists/ExperienceDenseSearchList'
import ExperienceSearchNoResults from './ExperienceSearchNoResults'

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const ListContent = styled(VerticalSpacer)<{ $compactTopSpacing: boolean }>`
  margin-top: 0 !important;
  padding-top: ${({ $compactTopSpacing }) => rem($compactTopSpacing ? 0 : 40)};

  ${mediaQueryDown.mobile} {
    padding-top: ${rem(8)};

    > * + * {
      margin-block-start: ${rem(8)} !important;
    }
  }
`

interface Props {
  list: App.ApiCallState<{
    ids: Array<string>
    placeId: string
    listKey?: string
    searchId?: string
    total?: number
    streamEnded?: boolean
  }>
  destinationName: string
  sortBy?: Experiences.SortTypes
  excludeIds?: Array<string>
  showNoResultsSummary?: boolean
  compactTopSpacing?: boolean
}

function ExperienceSearchPageList(props: Props) {
  const intl = useIntl()
  const { sortBy } = useExperiencesSortOptions()
  const { list, excludeIds } = props
  const { searchFilters } = useExperiencesSearchFilters()
  const validItemsLength = useMemo(() => list.ids.filter(id => !excludeIds?.includes(id)).length, [list.ids, excludeIds])
  const resultCount = excludeIds?.length
    ? validItemsLength
    : getExperienceListOfferCount(list)
  const isNoResults = !list.fetching && validItemsLength === 0
  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', 'Experiences Page Offer List'),
    { key: list.placeId },
  )

  return (
    <ListContent gap={16} $compactTopSpacing={!!props.compactTopSpacing}>
      {!isNoResults && (
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <Group direction="horizontal" verticalAlign="center">
            {resultCount > 0 && <BodyText variant="medium" colour="neutral-two">{intl.formatMessage(
              {
                id: 'tXFmCPud-a05Cl708Hm6',
                defaultMessage: '{count, plural, one {# experience} other {# experiences found}}',
                description: 'Pluralized label showing the number of experiences returned in search results',
              },
              { count: resultCount },
            )}
            </BodyText>}
            {list.fetching && <Loader inline />}
          </Group>
          <CSSBreakpoint min="tablet">
            <ExperienceSearchPageSort />
          </CSSBreakpoint>
        </Group>
      )}
      {isNoResults && <ExperienceSearchNoResults
        placeId={list.placeId}
        destinationName={props.destinationName}
        showNoResultsSummary={props.showNoResultsSummary}
      />}
      {!isNoResults && <OfferListEventsProvider tracking={tracking}>
        <ExperienceDenseSearchList
          sourceList={list}
          placeId={list.placeId}
          sortBy={sortBy}
          categoryCodes={searchFilters.categoryCodes}
          priceGte={searchFilters.priceGte}
          priceLte={searchFilters.priceLte}
          campaigns={searchFilters.campaigns}
          luxPlusFeatures={searchFilters.luxPlusFeatures}
          excludeIds={excludeIds}
          collections={searchFilters.collections}
          from={searchFilters.checkIn}
          to={searchFilters.checkOut}
          minRating={searchFilters.minRating}
          leExclusive={searchFilters.leExclusive}
          travelStyles={searchFilters.travelStyles}
          featured={searchFilters.featured}
          timeSlots={searchFilters.timeSlots}
          bounds={searchFilters.bounds}
        />
      </OfferListEventsProvider>}
    </ListContent>
  )
}

export default ExperienceSearchPageList
